import { createMiddleware } from "@tanstack/react-start";
import { getCookie, setCookie } from "@tanstack/react-start/server";
import type { UserRole } from "./lucia";
import { getLucia } from "./lucia";
import { runInUserContext } from "@/db/client";

export interface AuthContext {
  userId: string;
  email: string;
  role: UserRole;
}

async function readSessionFromCookie(): Promise<AuthContext | null> {
  const lucia = getLucia();
  const sessionId = getCookie(lucia.sessionCookieName);
  if (!sessionId) return null;

  const { session, user } = await lucia.validateSession(sessionId);
  if (!session) {
    const blank = lucia.createBlankSessionCookie();
    setCookie(blank.name, blank.value, blank.attributes);
    return null;
  }
  if (session.fresh) {
    const refreshed = lucia.createSessionCookie(session.id);
    setCookie(refreshed.name, refreshed.value, refreshed.attributes);
  }
  return { userId: user.id, email: user.email, role: user.role };
}

export const optionalAuth = createMiddleware({ type: "function" }).server(async ({ next }) => {
  const auth = await readSessionFromCookie();
  return next({ context: { auth } });
});

// requireAuth: проверяет cookie-сессию, оборачивает handler в transaction
// с `set local app.user_id = ${auth.userId}` — это даёт RLS политикам в
// db/migrations/0003_rls.sql правильный контекст. См. src/db/client.ts:
// getDb() внутри обёрнутого хендлера вернёт активную tx из ALS.
export const requireAuth = createMiddleware({ type: "function" }).server(async ({ next }) => {
  const auth = await readSessionFromCookie();
  if (!auth) {
    throw new Response("Unauthorized", { status: 401 });
  }
  return runInUserContext(auth.userId, () =>
    next({
      context: {
        auth,
        userId: auth.userId,
      },
    }),
  );
});

// requireAdmin: как requireAuth, но дополнительно проверяет role='admin'.
// Используется в server fn'ах admin-only страниц (создание юзеров и т.п.).
export const requireAdmin = createMiddleware({ type: "function" }).server(async ({ next }) => {
  const auth = await readSessionFromCookie();
  if (!auth) {
    throw new Response("Unauthorized", { status: 401 });
  }
  if (auth.role !== "admin") {
    throw new Response("Forbidden", { status: 403 });
  }
  return runInUserContext(auth.userId, () =>
    next({
      context: {
        auth,
        userId: auth.userId,
      },
    }),
  );
});

// requireAdminOrRop: как requireAuth, но пропускает только admin и rop (РОП).
// Для server fn'ов настроек, которые правит РОП (ценовые факторы, каталог,
// бренд). Запись на уровне БД дополнительно защищена RLS (is_admin OR is_rop).
export const requireAdminOrRop = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    const auth = await readSessionFromCookie();
    if (!auth) {
      throw new Response("Unauthorized", { status: 401 });
    }
    if (auth.role !== "admin" && auth.role !== "rop") {
      throw new Response("Forbidden", { status: 403 });
    }
    return runInUserContext(auth.userId, () =>
      next({
        context: {
          auth,
          userId: auth.userId,
        },
      }),
    );
  },
);
